import React from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { useTheme } from './ThemeProvider';

// Custom palette keys used by the AstraVision Sentinel shell/dashboard
// components (StatusChip, CameraSnapshotThumb, sentinel.panelElevated sx
// references, etc) -- augmented here since createMD3Theme() always
// populates them (see below).
declare module '@mui/material/styles' {
  interface Palette {
    sentinel: { panel: string; panelElevated: string; border: string };
    ai: Palette['primary'];
  }
  interface PaletteOptions {
    sentinel?: { panel: string; panelElevated: string; border: string };
    ai?: PaletteOptions['primary'];
  }
}

// Simple Material Design 3 provider for now
export const MaterialDesign3Provider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { theme, mode } = useTheme();
  const muiTheme = createMD3Theme(theme, mode);

  return (
    <MuiThemeProvider theme={muiTheme}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  );
};

// Material Design 3 color tokens for different themes
const md3ColorPalettes = {
  blue: {
    light: {
      primary: '#0061a4',
      onPrimary: '#ffffff',
      primaryContainer: '#d1e4ff',
      onPrimaryContainer: '#001d36',
      secondary: '#565f71',
      onSecondary: '#ffffff',
      secondaryContainer: '#dae2f9',
      onSecondaryContainer: '#131c2b',
      tertiary: '#705575',
      onTertiary: '#ffffff',
      tertiaryContainer: '#fbd7fc',
      onTertiaryContainer: '#28132e',
      error: '#ba1a1a',
      onError: '#ffffff',
      errorContainer: '#ffdad6',
      onErrorContainer: '#410002',
      background: '#fdfbff',
      onBackground: '#1a1c1e',
      surface: '#fdfbff',
      onSurface: '#1a1c1e',
      surfaceVariant: '#dfe2eb',
      onSurfaceVariant: '#43474e',
      outline: '#73777f',
      outlineVariant: '#c3c7cf',
      shadow: '#000000',
      scrim: '#000000',
      inverseSurface: '#2e3133',
      inverseOnSurface: '#f0f0f4',
      inversePrimary: '#9eca00',
      primaryFixed: '#d1e4ff',
      onPrimaryFixed: '#001d36',
      primaryFixedDim: '#9eca00',
      onPrimaryFixedVariant: '#00497d',
      secondaryFixed: '#dae2f9',
      onSecondaryFixed: '#131c2b',
      secondaryFixedDim: '#bec6dc',
      onSecondaryFixedVariant: '#3e4759',
      tertiaryFixed: '#fbd7fc',
      onTertiaryFixed: '#28132e',
      tertiaryFixedDim: '#ddbce0',
      onTertiaryFixedVariant: '#573e5c',
      surfaceDim: '#dad9dd',
      surfaceBright: '#fdfbff',
      surfaceContainerLowest: '#ffffff',
      surfaceContainerLow: '#f4f3f7',
      surfaceContainer: '#eeedf1',
      surfaceContainerHigh: '#e8e7ec',
      surfaceContainerHighest: '#e2e2e6',
    },
    dark: {
      primary: '#9eca00',
      onPrimary: '#003257',
      primaryContainer: '#00497d',
      onPrimaryContainer: '#d1e4ff',
      secondary: '#bec6dc',
      onSecondary: '#283041',
      secondaryContainer: '#3e4759',
      onSecondaryContainer: '#dae2f9',
      tertiary: '#ddbce0',
      onTertiary: '#3e2844',
      tertiaryContainer: '#573e5c',
      onTertiaryContainer: '#fbd7fc',
      error: '#ffb4ab',
      onError: '#690005',
      errorContainer: '#93000a',
      onErrorContainer: '#ffb4ab',
      background: '#0f1419',
      onBackground: '#e1e2e5',
      surface: '#0f1419',
      onSurface: '#e1e2e5',
      surfaceVariant: '#43474e',
      onSurfaceVariant: '#c3c7cf',
      outline: '#8d9199',
      outlineVariant: '#43474e',
      shadow: '#000000',
      scrim: '#000000',
      inverseSurface: '#e1e2e5',
      inverseOnSurface: '#2e3133',
      inversePrimary: '#0061a4',
      primaryFixed: '#d1e4ff',
      onPrimaryFixed: '#001d36',
      primaryFixedDim: '#9eca00',
      onPrimaryFixedVariant: '#00497d',
      secondaryFixed: '#dae2f9',
      onSecondaryFixed: '#131c2b',
      secondaryFixedDim: '#bec6dc',
      onSecondaryFixedVariant: '#3e4759',
      tertiaryFixed: '#fbd7fc',
      onTertiaryFixed: '#28132e',
      tertiaryFixedDim: '#ddbce0',
      onTertiaryFixedVariant: '#573e5c',
      surfaceDim: '#0f1419',
      surfaceBright: '#34393e',
      surfaceContainerLowest: '#0a0f14',
      surfaceContainerLow: '#181c21',
      surfaceContainer: '#1c2025',
      surfaceContainerHigh: '#262b2f',
      surfaceContainerHighest: '#31353a',
    },
  },
  green: {
    light: {
      primary: '#146c2e',
      onPrimary: '#ffffff',
      primaryContainer: '#a6f5a9',
      onPrimaryContainer: '#002108',
      secondary: '#52634f',
      onSecondary: '#ffffff',
      secondaryContainer: '#d5e8cf',
      onSecondaryContainer: '#101f0f',
      tertiary: '#386666',
      onTertiary: '#ffffff',
      tertiaryContainer: '#bcece0',
      onTertiaryContainer: '#00201a',
      error: '#ba1a1a',
      onError: '#ffffff',
      errorContainer: '#ffdad6',
      onErrorContainer: '#410002',
      background: '#f9fdec',
      onBackground: '#0f1f11',
      surface: '#f9fdec',
      onSurface: '#0f1f11',
      surfaceVariant: '#dbe5d9',
      onSurfaceVariant: '#3e4e3c',
      outline: '#6d7c6a',
      outlineVariant: '#becfbb',
      shadow: '#000000',
      scrim: '#000000',
      inverseSurface: '#232f22',
      inverseOnSurface: '#eff5e9',
      inversePrimary: '#8bd88d',
      primaryFixed: '#a6f5a9',
      onPrimaryFixed: '#002108',
      primaryFixedDim: '#8bd88d',
      onPrimaryFixedVariant: '#005316',
      secondaryFixed: '#d5e8cf',
      onSecondaryFixed: '#101f0f',
      secondaryFixedDim: '#b9ccb1',
      onSecondaryFixedVariant: '#3a4b37',
      tertiaryFixed: '#bcece0',
      onTertiaryFixed: '#00201a',
      tertiaryFixedDim: '#9fd0c4',
      onTertiaryFixedVariant: '#1f4e4c',
      surfaceDim: '#d9e8d5',
      surfaceBright: '#f9fdec',
      surfaceContainerLowest: '#ffffff',
      surfaceContainerLow: '#f3f5f0',
      surfaceContainer: '#edeff0',
      surfaceContainerHigh: '#e7e9e5',
      surfaceContainerHighest: '#e1e3df',
    },
    dark: {
      primary: '#7fdb6a',
      onPrimary: '#003910',
      primaryContainer: '#005316',
      onPrimaryContainer: '#a6f5a9',
      secondary: '#b9ccb1',
      onSecondary: '#243425',
      secondaryContainer: '#3a4b37',
      onSecondaryContainer: '#d5e8cf',
      tertiary: '#9fd0c4',
      onTertiary: '#003735',
      tertiaryContainer: '#1f4e4c',
      onTertiaryContainer: '#bcece0',
      error: '#ffb4ab',
      onError: '#690005',
      errorContainer: '#93000a',
      onErrorContainer: '#ffb4ab',
      background: '#0a1308',
      onBackground: '#e4f1de',
      surface: '#0a1308',
      onSurface: '#e4f1de',
      surfaceVariant: '#3e4e3c',
      onSurfaceVariant: '#becfbb',
      outline: '#889582',
      outlineVariant: '#3e4e3c',
      shadow: '#000000',
      scrim: '#000000',
      inverseSurface: '#e4f1de',
      inverseOnSurface: '#232f22',
      inversePrimary: '#146c2e',
      primaryFixed: '#a6f5a9',
      onPrimaryFixed: '#002108',
      primaryFixedDim: '#7fdb6a',
      onPrimaryFixedVariant: '#005316',
      secondaryFixed: '#d5e8cf',
      onSecondaryFixed: '#101f0f',
      secondaryFixedDim: '#b9ccb1',
      onSecondaryFixedVariant: '#3a4b37',
      tertiaryFixed: '#bcece0',
      onTertiaryFixed: '#00201a',
      tertiaryFixedDim: '#9fd0c4',
      onTertiaryFixedVariant: '#1f4e4c',
      surfaceDim: '#0a1308',
      surfaceBright: '#2e382a',
      surfaceContainerLowest: '#040f04',
      surfaceContainerLow: '#111b0f',
      surfaceContainer: '#151f13',
      surfaceContainerHigh: '#1f291d',
      surfaceContainerHighest: '#2a3326',
    },
  },
  purple: {
    light: {
      primary: '#6b46c1',
      onPrimary: '#ffffff',
      primaryContainer: '#e9ddff',
      onPrimaryContainer: '#22005d',
      secondary: '#5c5f61',
      onSecondary: '#ffffff',
      secondaryContainer: '#e0e2e5',
      onSecondaryContainer: '#181c1e',
      tertiary: '#76517a',
      onTertiary: '#ffffff',
      tertiaryContainer: '#ffd6f9',
      onTertiaryContainer: '#2d122c',
      error: '#ba1a1a',
      onError: '#ffffff',
      errorContainer: '#ffdad6',
      onErrorContainer: '#410002',
      background: '#fef7ff',
      onBackground: '#1d1a22',
      surface: '#fef7ff',
      onSurface: '#1d1a22',
      surfaceVariant: '#e7e0ec',
      onSurfaceVariant: '#49454e',
      outline: '#7a757f',
      outlineVariant: '#cac4d0',
      shadow: '#000000',
      scrim: '#000000',
      inverseSurface: '#322f38',
      inverseOnSurface: '#f5eef7',
      inversePrimary: '#d3bcff',
      primaryFixed: '#e9ddff',
      onPrimaryFixed: '#22005d',
      primaryFixedDim: '#cfbcff',
      onPrimaryFixedVariant: '#53358c',
      secondaryFixed: '#e0e2e5',
      onSecondaryFixed: '#181c1e',
      secondaryFixedDim: '#c1c9c2',
      onSecondaryFixedVariant: '#43474a',
      tertiaryFixed: '#ffd6f9',
      onTertiaryFixed: '#2d122c',
      tertiaryFixedDim: '#e5bad8',
      onTertiaryFixedVariant: '#5d3d60',
      surfaceDim: '#dfd8e2',
      surfaceBright: '#fef7ff',
      surfaceContainerLowest: '#ffffff',
      surfaceContainerLow: '#f9f2fb',
      surfaceContainer: '#f3ecf6',
      surfaceContainerHigh: '#ede6f0',
      surfaceContainerHighest: '#e7e0ea',
    },
    dark: {
      primary: '#cfbcff',
      onPrimary: '#381e72',
      primaryContainer: '#53358c',
      onPrimaryContainer: '#e9ddff',
      secondary: '#c1c9c2',
      onSecondary: '#2d3133',
      secondaryContainer: '#43474a',
      onSecondaryContainer: '#e0e2e5',
      tertiary: '#e5bad8',
      onTertiary: '#44263b',
      tertiaryContainer: '#5d3d60',
      onTertiaryContainer: '#ffd6f9',
      error: '#ffb4ab',
      onError: '#690005',
      errorContainer: '#93000a',
      onErrorContainer: '#ffb4ab',
      background: '#141218',
      onBackground: '#e6e1e9',
      surface: '#141218',
      onSurface: '#e6e1e9',
      surfaceVariant: '#49454e',
      onSurfaceVariant: '#cac4d0',
      outline: '#948f99',
      outlineVariant: '#49454e',
      shadow: '#000000',
      scrim: '#000000',
      inverseSurface: '#e6e1e9',
      inverseOnSurface: '#322f38',
      inversePrimary: '#6b46c1',
      primaryFixed: '#e9ddff',
      onPrimaryFixed: '#22005d',
      primaryFixedDim: '#cfbcff',
      onPrimaryFixedVariant: '#53358c',
      secondaryFixed: '#e0e2e5',
      onSecondaryFixed: '#181c1e',
      secondaryFixedDim: '#c1c9c2',
      onSecondaryFixedVariant: '#43474a',
      tertiaryFixed: '#ffd6f9',
      onTertiaryFixed: '#2d122c',
      tertiaryFixedDim: '#e5bad8',
      onTertiaryFixedVariant: '#5d3d60',
      surfaceDim: '#141218',
      surfaceBright: '#3b3841',
      surfaceContainerLowest: '#0e0d13',
      surfaceContainerLow: '#1c1a20',
      surfaceContainer: '#201e24',
      surfaceContainerHigh: '#2a282e',
      surfaceContainerHighest: '#353238',
    },
  },
  orange: {
    light: {
      primary: '#8b5000',
      onPrimary: '#ffffff',
      primaryContainer: '#ffdcba',
      onPrimaryContainer: '#2c1600',
      secondary: '#5f5f5f',
      onSecondary: '#ffffff',
      secondaryContainer: '#e3e2e0',
      onSecondaryContainer: '#1b1c1a',
      tertiary: '#6c5d2f',
      onTertiary: '#ffffff',
      tertiaryContainer: '#f5e1a7',
      onTertiaryContainer: '#231b00',
      error: '#ba1a1a',
      onError: '#ffffff',
      errorContainer: '#ffdad6',
      onErrorContainer: '#410002',
      background: '#fffbf7',
      onBackground: '#1f1b13',
      surface: '#fffbf7',
      onSurface: '#1f1b13',
      surfaceVariant: '#f0e0d0',
      onSurfaceVariant: '#4e4639',
      outline: '#807567',
      outlineVariant: '#d4c7b6',
      shadow: '#000000',
      scrim: '#000000',
      inverseSurface: '#353027',
      inverseOnSurface: '#faeee0',
      inversePrimary: '#ffb945',
      primaryFixed: '#ffdcba',
      onPrimaryFixed: '#2c1600',
      primaryFixedDim: '#ffb945',
      onPrimaryFixedVariant: '#6e3a00',
      secondaryFixed: '#e3e2e0',
      onSecondaryFixed: '#1b1c1a',
      secondaryFixedDim: '#c1c9c2',
      onSecondaryFixedVariant: '#464745',
      tertiaryFixed: '#f5e1a7',
      onTertiaryFixed: '#231b00',
      tertiaryFixedDim: '#d8c58d',
      onTertiaryFixedVariant: '#524517',
      surfaceDim: '#e1d9cc',
      surfaceBright: '#fffbf7',
      surfaceContainerLowest: '#ffffff',
      surfaceContainerLow: '#fbf5ef',
      surfaceContainer: '#f5f0e9',
      surfaceContainerHigh: '#efeae3',
      surfaceContainerHighest: '#e9e4dd',
    },
    dark: {
      primary: '#ffb945',
      onPrimary: '#4a2800',
      primaryContainer: '#6e3a00',
      onPrimaryContainer: '#ffdcba',
      secondary: '#c1c9c2',
      onSecondary: '#30312f',
      secondaryContainer: '#464745',
      onSecondaryContainer: '#e3e2e0',
      tertiary: '#d8c58d',
      onTertiary: '#3a2f05',
      tertiaryContainer: '#524517',
      onTertiaryContainer: '#f5e1a7',
      error: '#ffb4ab',
      onError: '#690005',
      errorContainer: '#93000a',
      onErrorContainer: '#ffb4ab',
      background: '#16130b',
      onBackground: '#e9e4dd',
      surface: '#16130b',
      onSurface: '#e9e4dd',
      surfaceVariant: '#4e4639',
      onSurfaceVariant: '#d4c7b6',
      outline: '#9d927f',
      outlineVariant: '#4e4639',
      shadow: '#000000',
      scrim: '#000000',
      inverseSurface: '#e9e4dd',
      inverseOnSurface: '#353027',
      inversePrimary: '#8b5000',
      primaryFixed: '#ffdcba',
      onPrimaryFixed: '#2c1600',
      primaryFixedDim: '#ffb945',
      onPrimaryFixedVariant: '#6e3a00',
      secondaryFixed: '#e3e2e0',
      onSecondaryFixed: '#1b1c1a',
      secondaryFixedDim: '#c1c9c2',
      onSecondaryFixedVariant: '#464745',
      tertiaryFixed: '#f5e1a7',
      onTertiaryFixed: '#231b00',
      tertiaryFixedDim: '#d8c58d',
      onTertiaryFixedVariant: '#524517',
      surfaceDim: '#16130b',
      surfaceBright: '#3d392f',
      surfaceContainerLowest: '#110e06',
      surfaceContainerLow: '#1f1b13',
      surfaceContainer: '#231f17',
      surfaceContainerHigh: '#2e2a21',
      surfaceContainerHighest: '#39352b',
    },
  },
  teal: {
    light: {
      primary: '#006a6a',
      onPrimary: '#ffffff',
      primaryContainer: '#6ff6f0',
      onPrimaryContainer: '#00201e',
      secondary: '#4a6363',
      onSecondary: '#ffffff',
      secondaryContainer: '#cce8e7',
      onSecondaryContainer: '#051f1f',
      tertiary: '#4b607c',
      onTertiary: '#ffffff',
      tertiaryContainer: '#d1e4ff',
      onTertiaryContainer: '#001d35',
      error: '#ba1a1a',
      onError: '#ffffff',
      errorContainer: '#ffdad6',
      onErrorContainer: '#410002',
      background: '#f3fffe',
      onBackground: '#051f1f',
      surface: '#f3fffe',
      onSurface: '#051f1f',
      surfaceVariant: '#d9e5e4',
      onSurfaceVariant: '#3d4e4e',
      outline: '#6d7a7a',
      outlineVariant: '#bcd3d2',
      shadow: '#000000',
      scrim: '#000000',
      inverseSurface: '#1a3434',
      inverseOnSurface: '#ecf2f1',
      inversePrimary: '#4dd0e1',
      primaryFixed: '#6ff6f0',
      onPrimaryFixed: '#00201e',
      primaryFixedDim: '#4dd0e1',
      onPrimaryFixedVariant: '#004f4f',
      secondaryFixed: '#cce8e7',
      onSecondaryFixed: '#051f1f',
      secondaryFixedDim: '#aec9c8',
      onSecondaryFixedVariant: '#334b4b',
      tertiaryFixed: '#d1e4ff',
      onTertiaryFixed: '#001d35',
      tertiaryFixedDim: '#aec9e8',
      onTertiaryFixedVariant: '#334863',
      surfaceDim: '#d3dbda',
      surfaceBright: '#f3fffe',
      surfaceContainerLowest: '#ffffff',
      surfaceContainerLow: '#edf4f3',
      surfaceContainer: '#e7eeed',
      surfaceContainerHigh: '#e1e9e8',
      surfaceContainerHighest: '#dbe3e2',
    },
    dark: {
      primary: '#4dd0e1',
      onPrimary: '#003738',
      primaryContainer: '#004f4f',
      onPrimaryContainer: '#6ff6f0',
      secondary: '#aec9c8',
      onSecondary: '#1a3434',
      secondaryContainer: '#334b4b',
      onSecondaryContainer: '#cce8e7',
      tertiary: '#aec9e8',
      onTertiary: '#18324a',
      tertiaryContainer: '#334863',
      onTertiaryContainer: '#d1e4ff',
      error: '#ffb4ab',
      onError: '#690005',
      errorContainer: '#93000a',
      onErrorContainer: '#ffb4ab',
      background: '#001f20',
      onBackground: '#dbe3e2',
      surface: '#001f20',
      onSurface: '#dbe3e2',
      surfaceVariant: '#3d4e4e',
      onSurfaceVariant: '#bcd3d2',
      outline: '#869696',
      outlineVariant: '#3d4e4e',
      shadow: '#000000',
      scrim: '#000000',
      inverseSurface: '#dbe3e2',
      inverseOnSurface: '#1a3434',
      inversePrimary: '#006a6a',
      primaryFixed: '#6ff6f0',
      onPrimaryFixed: '#00201e',
      primaryFixedDim: '#4dd0e1',
      onPrimaryFixedVariant: '#004f4f',
      secondaryFixed: '#cce8e7',
      onSecondaryFixed: '#051f1f',
      secondaryFixedDim: '#aec9c8',
      onSecondaryFixedVariant: '#334b4b',
      tertiaryFixed: '#d1e4ff',
      onTertiaryFixed: '#001d35',
      tertiaryFixedDim: '#aec9e8',
      onTertiaryFixedVariant: '#334863',
      surfaceDim: '#001f20',
      surfaceBright: '#253535',
      surfaceContainerLowest: '#000f10',
      surfaceContainerLow: '#0a1a1a',
      surfaceContainer: '#0e1f1f',
      surfaceContainerHigh: '#182b2b',
      surfaceContainerHighest: '#233636',
    },
  },
  windows: {
    light: {
      primary: '#0078d4',
      onPrimary: '#ffffff',
      primaryContainer: '#d6e9ff',
      onPrimaryContainer: '#001b3e',
      secondary: '#565f71',
      onSecondary: '#ffffff',
      secondaryContainer: '#dae2f9',
      onSecondaryContainer: '#131c2b',
      tertiary: '#705575',
      onTertiary: '#ffffff',
      tertiaryContainer: '#fbd7fc',
      onTertiaryContainer: '#28132e',
      error: '#ba1a1a',
      onError: '#ffffff',
      errorContainer: '#ffdad6',
      onErrorContainer: '#410002',
      background: '#fafbff',
      onBackground: '#1a1c1e',
      surface: '#fafbff',
      onSurface: '#1a1c1e',
      surfaceVariant: '#dfe2eb',
      onSurfaceVariant: '#43474e',
      outline: '#73777f',
      outlineVariant: '#c3c7cf',
      shadow: '#000000',
      scrim: '#000000',
      inverseSurface: '#2e3133',
      inverseOnSurface: '#f0f0f4',
      inversePrimary: '#aac7ff',
      primaryFixed: '#d6e9ff',
      onPrimaryFixed: '#001b3e',
      primaryFixedDim: '#aac7ff',
      onPrimaryFixedVariant: '#004a77',
      secondaryFixed: '#dae2f9',
      onSecondaryFixed: '#131c2b',
      secondaryFixedDim: '#bec6dc',
      onSecondaryFixedVariant: '#3e4759',
      tertiaryFixed: '#fbd7fc',
      onTertiaryFixed: '#28132e',
      tertiaryFixedDim: '#ddbce0',
      onTertiaryFixedVariant: '#573e5c',
      surfaceDim: '#dad9dd',
      surfaceBright: '#fafbff',
      surfaceContainerLowest: '#ffffff',
      surfaceContainerLow: '#f4f3f7',
      surfaceContainer: '#eeedf1',
      surfaceContainerHigh: '#e8e7ec',
      surfaceContainerHighest: '#e2e2e6',
    },
    dark: {
      primary: '#60cdff',
      onPrimary: '#003257',
      primaryContainer: '#00497d',
      onPrimaryContainer: '#d1e4ff',
      secondary: '#bec6dc',
      onSecondary: '#283041',
      secondaryContainer: '#3e4759',
      onSecondaryContainer: '#dae2f9',
      tertiary: '#ddbce0',
      onTertiary: '#3e2844',
      tertiaryContainer: '#573e5c',
      onTertiaryContainer: '#fbd7fc',
      error: '#ffb4ab',
      onError: '#690005',
      errorContainer: '#93000a',
      onErrorContainer: '#ffb4ab',
      background: '#0f1419',
      onBackground: '#e1e2e5',
      surface: '#0f1419',
      onSurface: '#e1e2e5',
      surfaceVariant: '#43474e',
      onSurfaceVariant: '#c3c7cf',
      outline: '#8d9199',
      outlineVariant: '#43474e',
      shadow: '#000000',
      scrim: '#000000',
      inverseSurface: '#e1e2e5',
      inverseOnSurface: '#2e3133',
      inversePrimary: '#0061a4',
      primaryFixed: '#d1e4ff',
      onPrimaryFixed: '#001d36',
      primaryFixedDim: '#9eca00',
      onPrimaryFixedVariant: '#00497d',
      secondaryFixed: '#dae2f9',
      onSecondaryFixed: '#131c2b',
      secondaryFixedDim: '#bec6dc',
      onSecondaryFixedVariant: '#3e4759',
      tertiaryFixed: '#fbd7fc',
      onTertiaryFixed: '#28132e',
      tertiaryFixedDim: '#ddbce0',
      onTertiaryFixedVariant: '#573e5c',
      surfaceDim: '#0f1419',
      surfaceBright: '#34393e',
      surfaceContainerLowest: '#0a0f14',
      surfaceContainerLow: '#181c21',
      surfaceContainer: '#1c2025',
      surfaceContainerHigh: '#262b2f',
      surfaceContainerHighest: '#31353a',
    },
  },
  mac: {
    light: {
      primary: '#007aff',
      onPrimary: '#ffffff',
      primaryContainer: '#d1e4ff',
      onPrimaryContainer: '#001d36',
      secondary: '#5c5f61',
      onSecondary: '#ffffff',
      secondaryContainer: '#e0e2e5',
      onSecondaryContainer: '#181c1e',
      tertiary: '#76517a',
      onTertiary: '#ffffff',
      tertiaryContainer: '#ffd6f9',
      onTertiaryContainer: '#2d122c',
      error: '#ba1a1a',
      onError: '#ffffff',
      errorContainer: '#ffdad6',
      onErrorContainer: '#410002',
      background: '#f5f5f7',
      onBackground: '#1d1d1f',
      surface: '#f5f5f7',
      onSurface: '#1d1d1f',
      surfaceVariant: '#e7e0ec',
      onSurfaceVariant: '#49454e',
      outline: '#7a757f',
      outlineVariant: '#cac4d0',
      shadow: '#000000',
      scrim: '#000000',
      inverseSurface: '#322f38',
      inverseOnSurface: '#f5eef7',
      inversePrimary: '#9eca00',
      primaryFixed: '#d1e4ff',
      onPrimaryFixed: '#001d36',
      primaryFixedDim: '#9eca00',
      onPrimaryFixedVariant: '#00497d',
      secondaryFixed: '#e0e2e5',
      onSecondaryFixed: '#181c1e',
      secondaryFixedDim: '#c1c9c2',
      onSecondaryFixedVariant: '#43474a',
      tertiaryFixed: '#ffd6f9',
      onTertiaryFixed: '#2d122c',
      tertiaryFixedDim: '#e5bad8',
      onTertiaryFixedVariant: '#5d3d60',
      surfaceDim: '#dfd8e2',
      surfaceBright: '#f5f5f7',
      surfaceContainerLowest: '#ffffff',
      surfaceContainerLow: '#f9f2fb',
      surfaceContainer: '#f3ecf6',
      surfaceContainerHigh: '#ede6f0',
      surfaceContainerHighest: '#e7e0ea',
    },
    dark: {
      primary: '#0a84ff',
      onPrimary: '#003257',
      primaryContainer: '#00497d',
      onPrimaryContainer: '#d1e4ff',
      secondary: '#c1c9c2',
      onSecondary: '#2d3133',
      secondaryContainer: '#43474a',
      onSecondaryContainer: '#e0e2e5',
      tertiary: '#e5bad8',
      onTertiary: '#44263b',
      tertiaryContainer: '#5d3d60',
      onTertiaryContainer: '#ffd6f9',
      error: '#ffb4ab',
      onError: '#690005',
      errorContainer: '#93000a',
      onErrorContainer: '#ffb4ab',
      background: '#000000',
      onBackground: '#e6e1e9',
      surface: '#000000',
      onSurface: '#e6e1e9',
      surfaceVariant: '#49454e',
      onSurfaceVariant: '#cac4d0',
      outline: '#948f99',
      outlineVariant: '#49454e',
      shadow: '#000000',
      scrim: '#000000',
      inverseSurface: '#e6e1e9',
      inverseOnSurface: '#322f38',
      inversePrimary: '#007aff',
      primaryFixed: '#d1e4ff',
      onPrimaryFixed: '#001d36',
      primaryFixedDim: '#9eca00',
      onPrimaryFixedVariant: '#00497d',
      secondaryFixed: '#e0e2e5',
      onSecondaryFixed: '#181c1e',
      secondaryFixedDim: '#c1c9c2',
      onSecondaryFixedVariant: '#43474a',
      tertiaryFixed: '#ffd6f9',
      onTertiaryFixed: '#2d122c',
      tertiaryFixedDim: '#e5bad8',
      onTertiaryFixedVariant: '#5d3d60',
      surfaceDim: '#000000',
      surfaceBright: '#3b3841',
      surfaceContainerLowest: '#0e0d13',
      surfaceContainerLow: '#1c1a20',
      surfaceContainer: '#201e24',
      surfaceContainerHigh: '#2a282e',
      surfaceContainerHighest: '#353238',
    },
  },
  linux: {
    light: {
      primary: '#2d5016',
      onPrimary: '#ffffff',
      primaryContainer: '#b0f397',
      onPrimaryContainer: '#072100',
      secondary: '#55624c',
      onSecondary: '#ffffff',
      secondaryContainer: '#d8e7cb',
      onSecondaryContainer: '#131f0e',
      tertiary: '#3a665e',
      onTertiary: '#ffffff',
      tertiaryContainer: '#bcece0',
      onTertiaryContainer: '#00201a',
      error: '#ba1a1a',
      onError: '#ffffff',
      errorContainer: '#ffdad6',
      onErrorContainer: '#410002',
      background: '#f7f9f7',
      onBackground: '#1a1c19',
      surface: '#f7f9f7',
      onSurface: '#1a1c19',
      surfaceVariant: '#dee5d8',
      onSurfaceVariant: '#42493f',
      outline: '#717970',
      outlineVariant: '#c1c9bd',
      shadow: '#000000',
      scrim: '#000000',
      inverseSurface: '#2e312d',
      inverseOnSurface: '#f0f2ed',
      inversePrimary: '#95d683',
      primaryFixed: '#b0f397',
      onPrimaryFixed: '#072100',
      primaryFixedDim: '#95d683',
      onPrimaryFixedVariant: '#1a4000',
      secondaryFixed: '#d8e7cb',
      onSecondaryFixed: '#131f0e',
      secondaryFixedDim: '#bccbb0',
      onSecondaryFixedVariant: '#3e4a35',
      tertiaryFixed: '#bcece0',
      onTertiaryFixed: '#00201a',
      tertiaryFixedDim: '#a0d0c4',
      onTertiaryFixedVariant: '#224e45',
      surfaceDim: '#d7d9d4',
      surfaceBright: '#f7f9f7',
      surfaceContainerLowest: '#ffffff',
      surfaceContainerLow: '#f1f3ee',
      surfaceContainer: '#ebeee8',
      surfaceContainerHigh: '#e5e8e2',
      surfaceContainerHighest: '#dfe2dc',
    },
    dark: {
      primary: '#4ade80',
      onPrimary: '#0d3900',
      primaryContainer: '#1a4000',
      onPrimaryContainer: '#b0f397',
      secondary: '#bccbb0',
      onSecondary: '#27341f',
      secondaryContainer: '#3e4a35',
      onSecondaryContainer: '#d8e7cb',
      tertiary: '#a0d0c4',
      onTertiary: '#07372f',
      tertiaryContainer: '#224e45',
      onTertiaryContainer: '#bcece0',
      error: '#ffb4ab',
      onError: '#690005',
      errorContainer: '#93000a',
      onErrorContainer: '#ffb4ab',
      background: '#0f172a',
      onBackground: '#e1e2e5',
      surface: '#0f172a',
      onSurface: '#e1e2e5',
      surfaceVariant: '#43474e',
      onSurfaceVariant: '#c3c7cf',
      outline: '#8d9199',
      outlineVariant: '#43474e',
      shadow: '#000000',
      scrim: '#000000',
      inverseSurface: '#e1e2e5',
      inverseOnSurface: '#2e3133',
      inversePrimary: '#2d5016',
      primaryFixed: '#b0f397',
      onPrimaryFixed: '#072100',
      primaryFixedDim: '#95d683',
      onPrimaryFixedVariant: '#1a4000',
      secondaryFixed: '#d8e7cb',
      onSecondaryFixed: '#131f0e',
      secondaryFixedDim: '#bccbb0',
      onSecondaryFixedVariant: '#3e4a35',
      tertiaryFixed: '#bcece0',
      onTertiaryFixed: '#00201a',
      tertiaryFixedDim: '#a0d0c4',
      onTertiaryFixedVariant: '#224e45',
      surfaceDim: '#0f1419',
      surfaceBright: '#34393e',
      surfaceContainerLowest: '#0a0f14',
      surfaceContainerLow: '#181c21',
      surfaceContainer: '#1c2025',
      surfaceContainerHigh: '#262b2f',
      surfaceContainerHighest: '#31353a',
    },
  },
  office: {
    light: {
      primary: '#0078d4',
      onPrimary: '#ffffff',
      primaryContainer: '#d6e9ff',
      onPrimaryContainer: '#001b3e',
      secondary: '#565f71',
      onSecondary: '#ffffff',
      secondaryContainer: '#dae2f9',
      onSecondaryContainer: '#131c2b',
      tertiary: '#6c5d2f',
      onTertiary: '#ffffff',
      tertiaryContainer: '#f5e1a7',
      onTertiaryContainer: '#231b00',
      error: '#ba1a1a',
      onError: '#ffffff',
      errorContainer: '#ffdad6',
      onErrorContainer: '#410002',
      background: '#fafbff',
      onBackground: '#1a1c1e',
      surface: '#fafbff',
      onSurface: '#1a1c1e',
      surfaceVariant: '#dfe2eb',
      onSurfaceVariant: '#43474e',
      outline: '#73777f',
      outlineVariant: '#c3c7cf',
      shadow: '#000000',
      scrim: '#000000',
      inverseSurface: '#2e3133',
      inverseOnSurface: '#f0f0f4',
      inversePrimary: '#aac7ff',
      primaryFixed: '#d6e9ff',
      onPrimaryFixed: '#001b3e',
      primaryFixedDim: '#aac7ff',
      onPrimaryFixedVariant: '#004a77',
      secondaryFixed: '#dae2f9',
      onSecondaryFixed: '#131c2b',
      secondaryFixedDim: '#bec6dc',
      onSecondaryFixedVariant: '#3e4759',
      tertiaryFixed: '#f5e1a7',
      onTertiaryFixed: '#231b00',
      tertiaryFixedDim: '#d8c58d',
      onTertiaryFixedVariant: '#524517',
      surfaceDim: '#dad9dd',
      surfaceBright: '#fafbff',
      surfaceContainerLowest: '#ffffff',
      surfaceContainerLow: '#f4f3f7',
      surfaceContainer: '#eeedf1',
      surfaceContainerHigh: '#e8e7ec',
      surfaceContainerHighest: '#e2e2e6',
    },
    dark: {
      primary: '#0078d4',
      onPrimary: '#003257',
      primaryContainer: '#00497d',
      onPrimaryContainer: '#d1e4ff',
      secondary: '#bec6dc',
      onSecondary: '#283041',
      secondaryContainer: '#3e4759',
      onSecondaryContainer: '#dae2f9',
      tertiary: '#d8c58d',
      onTertiary: '#3a2f05',
      tertiaryContainer: '#524517',
      onTertiaryContainer: '#f5e1a7',
      error: '#ffb4ab',
      onError: '#690005',
      errorContainer: '#93000a',
      onErrorContainer: '#ffb4ab',
      background: '#0f1419',
      onBackground: '#e1e2e5',
      surface: '#0f1419',
      onSurface: '#e1e2e5',
      surfaceVariant: '#43474e',
      onSurfaceVariant: '#c3c7cf',
      outline: '#8d9199',
      outlineVariant: '#43474e',
      shadow: '#000000',
      scrim: '#000000',
      inverseSurface: '#e1e2e5',
      inverseOnSurface: '#2e3133',
      inversePrimary: '#0061a4',
      primaryFixed: '#d1e4ff',
      onPrimaryFixed: '#001d36',
      primaryFixedDim: '#9eca00',
      onPrimaryFixedVariant: '#00497d',
      secondaryFixed: '#dae2f9',
      onSecondaryFixed: '#131c2b',
      secondaryFixedDim: '#bec6dc',
      onSecondaryFixedVariant: '#3e4759',
      tertiaryFixed: '#f5e1a7',
      onTertiaryFixed: '#231b00',
      tertiaryFixedDim: '#d8c58d',
      onTertiaryFixedVariant: '#524517',
      surfaceDim: '#0f1419',
      surfaceBright: '#34393e',
      surfaceContainerLowest: '#0a0f14',
      surfaceContainerLow: '#181c21',
      surfaceContainer: '#1c2025',
      surfaceContainerHigh: '#262b2f',
      surfaceContainerHighest: '#31353a',
    },
  },
};

export const createMD3Theme = (theme: 'blue' | 'green' | 'purple' | 'orange' | 'teal' | 'windows' | 'mac' | 'linux' | 'office', mode: 'light' | 'dark') => {
  const colors = md3ColorPalettes[theme][mode];

  return createTheme({
    palette: {
      mode,
      primary: {
        main: colors.primary,
        contrastText: colors.onPrimary,
      },
      secondary: {
        main: colors.secondary,
        contrastText: colors.onSecondary,
      },
      error: {
        main: colors.error,
        contrastText: colors.onError,
      },
      background: {
        default: colors.background,
        paper: colors.surface,
      },
      text: {
        primary: colors.onSurface,
        secondary: colors.onSurfaceVariant,
      },
      // Fixed brand accent tokens used by the AstraVision Sentinel shell/
      // dashboard components (StatusChip, CameraSnapshotThumb, etc) --
      // present regardless of which color preset/light-dark mode is
      // selected, so those components render consistently under the
      // restored multi-theme system instead of only under one fixed theme.
      ai: {
        main: '#9B7CFF',
        contrastText: '#080B12',
      },
      sentinel: {
        panel: mode === 'dark' ? '#101722' : '#F4F6FA',
        panelElevated: mode === 'dark' ? '#151E2C' : '#FFFFFF',
        border: mode === 'dark' ? '#243044' : colors.outlineVariant,
      },
    },
    typography: {
      fontFamily: [
        'Roboto',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Arial',
        'sans-serif',
      ].join(','),
      h1: {
        fontSize: '3.5rem',
        fontWeight: 400,
        lineHeight: 1.2,
        letterSpacing: '-0.01562em',
      },
      h2: {
        fontSize: '2.8125rem',
        fontWeight: 400,
        lineHeight: 1.2,
        letterSpacing: '-0.00833em',
      },
      h3: {
        fontSize: '2.25rem',
        fontWeight: 400,
        lineHeight: 1.2,
        letterSpacing: '0em',
      },
      h4: {
        fontSize: '1.875rem',
        fontWeight: 400,
        lineHeight: 1.2,
        letterSpacing: '0.00735em',
      },
      h5: {
        fontSize: '1.5rem',
        fontWeight: 400,
        lineHeight: 1.2,
        letterSpacing: '0em',
      },
      h6: {
        fontSize: '1.25rem',
        fontWeight: 500,
        lineHeight: 1.2,
        letterSpacing: '0.0075em',
      },
      subtitle1: {
        fontSize: '1rem',
        fontWeight: 400,
        lineHeight: 1.75,
        letterSpacing: '0.00938em',
      },
      subtitle2: {
        fontSize: '0.875rem',
        fontWeight: 500,
        lineHeight: 1.57,
        letterSpacing: '0.00714em',
      },
      body1: {
        fontSize: '1rem',
        fontWeight: 400,
        lineHeight: 1.5,
        letterSpacing: '0.00938em',
      },
      body2: {
        fontSize: '0.875rem',
        fontWeight: 400,
        lineHeight: 1.43,
        letterSpacing: '0.01071em',
      },
      button: {
        fontSize: '0.875rem',
        fontWeight: 500,
        lineHeight: 1.75,
        letterSpacing: '0.02857em',
        textTransform: 'none',
      },
      caption: {
        fontSize: '0.75rem',
        fontWeight: 400,
        lineHeight: 1.66,
        letterSpacing: '0.03333em',
      },
      overline: {
        fontSize: '0.75rem',
        fontWeight: 500,
        lineHeight: 2.66,
        letterSpacing: '0.08333em',
        textTransform: 'uppercase',
      },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 20,
            padding: '8px 24px',
            height: 40,
            fontWeight: 500,
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
          },
          contained: {
            '&:hover': {
              boxShadow: 'none',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: 'none',
            border: `1px solid ${colors.outlineVariant}`,
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            backgroundColor: 'transparent !important',
            color: 'inherit !important',
            WebkitTextFillColor: 'unset !important',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            height: 32,
          },
        },
      },
    },
  });
};
